import { createSelector } from 'reselect';
import { touch_object } from '../../js/app/react/helpers.objects';
import { RetirementAssetsAreaChart } from '../../js/app/react/chartDescriptors.financialPlan';
import isViewMobile from '../../js/app/react/isViewMobile';

let group;
let localChartData;
let goalData;
let isEmpty;
let dashIsBetweenGraphs = false;
let aabbRects = [];
let labelQueue = [];
const textLabelsToRemoveWhenRedrawing = [];
const domLines = [];
let localProps;
let localDescriptor;
const RECOMMENDED_SERIES_INDEX = 1;
const CURRENT_SERIES_INDEX = 2;
const RECOMMENDED_MARKERS_AND_HOVERS_INDEX = 3;
const CURRENT_MARKERS_AND_HOVERS_INDEX = 4;
const FUNDS_AT_RETIREMENT_RECTANGLE_SIZE = {
  w: 140,
  h: 50,
};

const COLOR_BLUE = '#0078d4';
const COLOR_LIGHT_BLUE = '#A7DCF1';
const COLOR_GREEN = '#007a5a';
const COLOR_LIGHT_GREEN = '#CAE3AF';
const COLOR_RED = '#C7094D';
const COLOR_LIGHT_RED = '#F7C8C6';

const canDrawLabel = (chart, x, y, w, h) => {
  let intersects = false;
  if (
    x < chart.xAxis[0].toPixels(0) - 16 ||
    x + w > chart.xAxis[0].toPixels(localChartData.CurrentSeries.length - 1) + 16
  ) {
    intersects = true;
  }
  aabbRects.forEach(rect => {
    if (x < rect.x + rect.w && x + w > rect.x && y < rect.y + rect.h && h + y > rect.y) {
      intersects = true;
    }
  });
  return !intersects;
};
const mouseOut = (chart, pIndex) => () => {
  if (ADV.util.isNumeric(pIndex)) {
    chart.tooltip.hide();
  }
};
const mouseIn = (chart, sIndex, pIndex) => () => {
  if (ADV.util.isNumeric(pIndex)) {
    chart.tooltip.refresh(chart.series[sIndex].points[pIndex]);
  }
};
const renderVerticalDashLine = (
  chart,
  verticalLine,
  ageLabelNumber,
  ageLabel,
  color,
  pIndex,
  sIndex,
  noLine,
) => {
  const path = ['M', verticalLine.x1, verticalLine.y1, 'L', verticalLine.x2, verticalLine.y2];

  if (!noLine) {
    // render the line
    if (dashIsBetweenGraphs) {
      const nextSeries = document.querySelector('.highcharts-series.highcharts-series-2');
      domLines.push(
        chart.renderer.path(path).attr({
          'stroke-width': 2,
          stroke: color,
          zIndex: 3,
          'stroke-dasharray': '5, 5',
        }).element,
      );
      nextSeries.parentNode.insertBefore(domLines[domLines.length - 1], nextSeries);
    } else {
      chart.renderer
        .path(path)
        .attr({
          'stroke-width': 2,
          stroke: color,
          zIndex: 3,
          'stroke-dasharray': '5, 5',
        })
        .add(group);
    }
  }

  // render the bottom circle
  chart.renderer
    .rect(verticalLine.x1 - 16, verticalLine.y1 - 10, 34, 20, 10)
    .attr({
      fill: color,
      zIndex: 5,
    })
    .add(group)
    .on('mouseover', mouseIn(chart, sIndex, pIndex))
    .on('mouseout', mouseOut(chart, pIndex));

  aabbRects.push({
    x: verticalLine.x1 - 16,
    y: verticalLine.y1 - 10,
    w: 34,
    h: 20,
  });

  // render the bottom age number
  chart.renderer
    .text(ageLabelNumber, verticalLine.x1, verticalLine.y1 + 5)
    .attr({
      align: 'center',
      zIndex: 5,
    })
    .css({
      fontSize: '14px',
      fontWeight: '500',
      color: 'white',
      fontFamily: 'Open Sans, helvetica, sans-serif',
    })
    .add(group)
    .on('mouseover', mouseIn(chart, sIndex, pIndex))
    .on('mouseout', mouseOut(chart, pIndex));

  const xPosition = verticalLine.x1;
  let yPosition = verticalLine.y1 + 4;
  let rectHeight = 30;
  const rectWidth = 100;
  let yRectPosition = yPosition - rectHeight / 2 - 5;

  if (/.*[<\s*]br\s*[\/][\s*>].*/.test(ageLabel)) {
    yPosition = verticalLine.y1 - 2;
  } else {
    rectHeight = 17;
    yRectPosition = yPosition - rectHeight / 2 - 5;
  }

  labelQueue.push({
    tl: `<div class="age-label left">${ageLabel}</div>`,
    tr: `<div class="age-label right">${ageLabel}</div>`,
    xPosition,
    yPosition,
    yRectPosition,
    rectWidth,
    rectHeight,
  });
};
const deriveRenderAttributes = (align, xPosition, rectWidth, tr, tl) => {
  if (align == 'right') {
    return {
      xRectPosition: xPosition + 20,
      xTextPosition: xPosition + 20,
      tAlign: 'left',
      t: tl,
    };
  }
  return {
    xRectPosition: xPosition - rectWidth - 20,
    xTextPosition: xPosition - 20,
    tAlign: 'right',
    t: tr,
  };
};
const findBestLabelAlignmentConfiguration = chart => {
  let best = [];
  let bestCount = 0;
  const results = labelQueue.map(() => '');
  const backtrackLabelAlignment = k => {
    if (k == results.length) {
      const lAabbRects = clone(aabbRects);
      let couldDraw = 0;
      labelQueue.forEach(({ xPosition, yRectPosition, rectWidth, rectHeight }, index) => {
        const { xRectPosition } = deriveRenderAttributes(results[index], xPosition, rectWidth);
        if (canDrawLabel(chart, xRectPosition, yRectPosition, rectWidth, rectHeight)) {
          couldDraw++;
        }
        aabbRects.push({
          x: xRectPosition,
          y: yRectPosition,
          w: rectWidth,
          h: rectHeight,
        });
      });

      if (couldDraw > bestCount) {
        bestCount = couldDraw;
        best = clone(results);
      }
      aabbRects = lAabbRects;
      return;
    }
    // Prefer left aligned labels first
    results[k] = 'left';
    backtrackLabelAlignment(k + 1);
    results[k] = 'right';
    backtrackLabelAlignment(k + 1);
  };
  backtrackLabelAlignment(0);
  return best;
};
const processLabelQueue = chart => {
  findBestLabelAlignmentConfiguration(chart).forEach((align, index) => {
    const { tl, tr, xPosition, yPosition, yRectPosition, rectWidth, rectHeight } = labelQueue[
      index
    ];
    const { xRectPosition, xTextPosition, t, tAlign } = deriveRenderAttributes(
      align,
      xPosition,
      rectWidth,
      tr,
      tl,
    );

    if (!canDrawLabel(chart, xRectPosition, yRectPosition, rectWidth, rectHeight)) {
      return;
    }

    textLabelsToRemoveWhenRedrawing.push(
      chart.renderer
        .text(t, xTextPosition, yPosition, true)
        .attr({
          align: tAlign,
        })
        .add(group),
    );

    // This white rect will draw over the dotted lines but not over the chart numbers (pills)
    chart.renderer
      .rect(xRectPosition, yRectPosition, rectWidth, rectHeight)
      .attr({
        fill: '#FFF',
        zIndex: 4,
      })
      .add(group);
    aabbRects.push({
      x: xRectPosition,
      y: yRectPosition,
      w: rectWidth,
      h: rectHeight,
    });
  });
};
const bottomLabelOffset = isSpouse => {
  let offset = 42;

  if (isSpouse) {
    offset = 77;
  }
  return offset;
};
const isPrincipalAlreadyRetired = () => {
  return localChartData.PrincipalRetirementIndex < 0;
};
const isSpouseAlreadyRetired = () => {
  return localChartData.SpouseRetirementIndex < 0;
};
const topYPoint = index => {
  return localChartData.RecommendedSeries[index];
};
const topYPointSeriesIndex = index => {
  return RECOMMENDED_MARKERS_AND_HOVERS_INDEX;
};
const renderFundsAtRetirementLabel = (chart, amountForRetirement, retirementIndexLine) => {
  // ADV-5867 - Hide the label when the amount is 0
  if (amountForRetirement == 0) {
    return;
  }

  let align;
  const possibleAlignments = [
    {
      align: 'right',
      x: retirementIndexLine.x1 - FUNDS_AT_RETIREMENT_RECTANGLE_SIZE.w,
      y: retirementIndexLine.y2 - 25,
      w: FUNDS_AT_RETIREMENT_RECTANGLE_SIZE.w,
      h: FUNDS_AT_RETIREMENT_RECTANGLE_SIZE.h,
    },
    {
      align: 'left',
      x: retirementIndexLine.x1,
      y: retirementIndexLine.y2 - 25,
      w: FUNDS_AT_RETIREMENT_RECTANGLE_SIZE.w,
      h: FUNDS_AT_RETIREMENT_RECTANGLE_SIZE.h,
    },
    {
      align: 'center',
      x: retirementIndexLine.x1 - FUNDS_AT_RETIREMENT_RECTANGLE_SIZE.w / 2,
      y: retirementIndexLine.y2 - 25,
      w: FUNDS_AT_RETIREMENT_RECTANGLE_SIZE.w,
      h: FUNDS_AT_RETIREMENT_RECTANGLE_SIZE.h,
    },
  ];

  possibleAlignments.forEach(alignment => {
    if (canDrawLabel(chart, alignment.x, alignment.y, alignment.w, alignment.h)) {
      align = alignment.align;
    }
  });

  chart.renderer
    .text(
      `$${ADV.util.number_format(amountForRetirement)}`,
      retirementIndexLine.x1,
      retirementIndexLine.y2 - 25,
    )
    .attr({
      align,
    })
    .css({
      fontSize: '20px',
      fontWeight: '700',
      color: '#0078d4',
      fontFamily: 'Open Sans, helvetica, sans-serif',
    })
    .add(group);

  // render the funds needed amount label 'Funds Needed For Retirement'
  chart.renderer
    .text('Funds at Retirement Start', retirementIndexLine.x1, retirementIndexLine.y2 - 47)
    .attr({
      align,
    })
    .css({
      fontSize: '11px',
      fontWeight: '600',
      color: '#0078d4',
      fontFamily: 'Open Sans, helvetica, sans-serif',
    })
    .add(group);
};
const renderChartLabels = function() {
  if (isViewMobile()) {
    return;
  }
  if (group) {
    group.destroy();
    // clean up after highcharts usehtml
    textLabelsToRemoveWhenRedrawing
      .splice(0, textLabelsToRemoveWhenRedrawing.length)
      .forEach(textLabel => {
        if (textLabel.element.parentNode) {
          textLabel.element.parentNode.removeChild(textLabel.element);
        }
      });
  }
  domLines.splice(0, domLines.length).forEach(domLine => {
    if (domLine.parentNode) {
      domLine.parentNode.removeChild(domLine);
    }
  });
  group = this.renderer
    .g()
    .attr({
      zIndex: 10,
      id: 'chart-labels',
    })
    .add();
  aabbRects = [];
  labelQueue = [];

  let principalRetirementLabel = `${localChartData.PrincipalName}'s <br/> Retirement Age`;
  let principalLifeExpectancyLabel = `${localChartData.PrincipalName}'s <br/> Life Expectancy`;

  if (!localChartData.HasSpouse) {
    principalRetirementLabel = 'Retirement Age';
    principalLifeExpectancyLabel = 'Life Expectancy';
  }
  let principalRetirementIndexLine;
  let spouseRetirementIndexLine;

  if (
    !isPrincipalAlreadyRetired() &&
    localChartData.PrincipalRetirementAge <= localChartData.PrincipalLifeExpectancy
  ) {
    principalRetirementIndexLine = {
      x1: this.xAxis[0].toPixels(localChartData.PrincipalRetirementIndex),
      x2: this.xAxis[0].toPixels(localChartData.PrincipalRetirementIndex),
      y1: this.yAxis[0].toPixels(0) + bottomLabelOffset(false),
      y2: this.yAxis[0].toPixels(topYPoint(localChartData.PrincipalRetirementIndex)),
    };
    renderVerticalDashLine(
      this,
      principalRetirementIndexLine,
      localChartData.PrincipalRetirementAge,
      principalRetirementLabel,
      COLOR_BLUE,
      undefined,
      undefined,
      localChartData.HasSpouse &&
        localChartData.SpouseRetirementAge <= localChartData.SpouseLifeExpectancy &&
        localChartData.PrincipalRetirementIndex == localChartData.SpouseRetirementIndex,
    );
  }

  renderVerticalDashLine(
    this,
    {
      x1: this.xAxis[0].toPixels(localChartData.PrincipalLifeExpectancyIndex),
      x2: this.xAxis[0].toPixels(localChartData.PrincipalLifeExpectancyIndex),
      y1: this.yAxis[0].toPixels(0) + bottomLabelOffset(false),
      y2: this.yAxis[0].toPixels(topYPoint(localChartData.PrincipalLifeExpectancyIndex)),
    },
    localChartData.PrincipalLifeExpectancy,
    principalLifeExpectancyLabel,
    topYPoint(localChartData.PrincipalLifeExpectancyIndex) > 0 ? COLOR_GREEN : COLOR_RED,
    localChartData.PrincipalLifeExpectancyIndex,
    topYPointSeriesIndex(localChartData.PrincipalLifeExpectancyIndex),
    localChartData.HasSpouse &&
      localChartData.PrincipalLifeExpectancyIndex == localChartData.SpouseLifeExpectancyIndex,
  );

  if (localChartData.HasSpouse) {
    if (
      !isSpouseAlreadyRetired() &&
      localChartData.SpouseRetirementAge <= localChartData.SpouseLifeExpectancy
    ) {
      spouseRetirementIndexLine = {
        x1: this.xAxis[0].toPixels(localChartData.SpouseRetirementIndex),
        x2: this.xAxis[0].toPixels(localChartData.SpouseRetirementIndex),
        y1: this.yAxis[0].toPixels(0) + bottomLabelOffset(true),
        y2: this.yAxis[0].toPixels(topYPoint(localChartData.SpouseRetirementIndex)),
      };
      renderVerticalDashLine(
        this,
        spouseRetirementIndexLine,
        localChartData.SpouseRetirementAge,
        `${localChartData.SpouseName}'s <br/> Retirement Age`,
        COLOR_BLUE,
      );
    }

    renderVerticalDashLine(
      this,
      {
        x1: this.xAxis[0].toPixels(localChartData.SpouseLifeExpectancyIndex),
        x2: this.xAxis[0].toPixels(localChartData.SpouseLifeExpectancyIndex),
        y1: this.yAxis[0].toPixels(0) + bottomLabelOffset(true),
        y2: this.yAxis[0].toPixels(topYPoint(localChartData.SpouseLifeExpectancyIndex)),
      },
      localChartData.SpouseLifeExpectancy,
      `${localChartData.SpouseName}'s <br/> Life Expectancy`,
      topYPoint(localChartData.SpouseLifeExpectancyIndex) > 0 ? COLOR_GREEN : COLOR_RED,
      localChartData.SpouseLifeExpectancyIndex,
      topYPointSeriesIndex(localChartData.SpouseLifeExpectancyIndex),
    );
  }

  if (
    spouseRetirementIndexLine &&
    (isPrincipalAlreadyRetired() ||
      localChartData.PrincipalRetirementIndex > localChartData.SpouseRetirementIndex)
  ) {
    renderFundsAtRetirementLabel(
      this,
      topYPoint(localChartData.SpouseRetirementIndex),
      spouseRetirementIndexLine,
    );
  } else if (principalRetirementIndexLine) {
    renderFundsAtRetirementLabel(
      this,
      topYPoint(localChartData.PrincipalRetirementIndex),
      principalRetirementIndexLine,
    );
  }

  processLabelQueue(this);
};
const seriesZIndex = (chartData, seriesIndex, otherSeriesIndex) => {
  const series = chartData.series[seriesIndex].data;
  const otherSeries = chartData.series[otherSeriesIndex].data;
  const seriesTopPoints = series.reduce(
    (acc, { y }, index) => acc + (y > otherSeries[index].y ? 1 : 0),
    0,
  );
  const otherSeriesTopPoints = otherSeries.reduce(
    (acc, { y }, index) => acc + (y > series[index].y ? 1 : 0),
    0,
  );
  if (seriesTopPoints > otherSeriesTopPoints) {
    return 1;
  }
  if (seriesTopPoints === otherSeriesTopPoints) {
    return seriesIndex < otherSeriesIndex ? 1 : 3;
  }
  return 3;
};
const yearCalculator = (categories, index) => {
  if (index < categories.length && index >= 0) {
    return categories[index];
  }
  return '';
};
const ageCalculator = (lifeExpectancy, lifeExpectancyIndex, index) => {
  const age =
    +lifeExpectancy -
    +localChartData.Categories[lifeExpectancyIndex] +
    +localChartData.Categories[index];
  if (age > lifeExpectancy) {
    return '-';
  }
  return age;
};

export const chartDataSelector = createSelector(
  [
    (state, { clientId, isPrint, isCurrentPlan }) => ({
      responseData: (state.RetirementAssetsChart[clientId] || {}).responseData,
      isPrint,
      isCurrentPlan,
    }),
  ],
  props => {
    const { isCurrentPlan } = props;
    if (ADV.util.deepEqual(props, localProps)) {
      return localDescriptor;
    }
    const { responseData, isPrint } = props;
    if (responseData == null || Object.keys(responseData).length < 1) {
      return;
    }

    goalData = responseData.GoalChart;

    const chartData = clone(RetirementAssetsAreaChart);

    let maxY = 0;

    chartData.chart.spacingBottom = 65;
    if (!goalData.Chart.Data.HasSpouse) {
      chartData.chart.spacingBottom = 30;
    }

    localChartData = {
      CurrentSeries: goalData.Chart.Data.Series.Current,
      RecommendedSeries: goalData.Chart.Data.Series.Recommended,
      Categories: goalData.Chart.Data.Categories,

      PrincipalName: goalData.Chart.Data.PrincipalName,
      PrincipalRetirementAge: goalData.Chart.Data.PrincipalRetirementAge,
      PrincipalRetirementIndex: goalData.Chart.Data.PrincipalRetirementIndex,
      PrincipalLifeExpectancy: goalData.Chart.Data.PrincipalLifeExpectancy,
      PrincipalLifeExpectancyIndex: goalData.Chart.Data.PrincipalLifeExpectancyIndex,

      HasSpouse: goalData.Chart.Data.HasSpouse,
      SpouseName: goalData.Chart.Data.SpouseName,
      SpouseRetirementAge: goalData.Chart.Data.SpouseRetirementAge,
      SpouseRetirementIndex: goalData.Chart.Data.SpouseRetirementIndex,
      SpouseLifeExpectancy: goalData.Chart.Data.SpouseLifeExpectancy,
      SpouseLifeExpectancyIndex: goalData.Chart.Data.SpouseLifeExpectancyIndex,

      IsPrint: isPrint,
    };
    dashIsBetweenGraphs = false;

    touch_object(chartData, 'xAxis');
    touch_object(chartData, 'yAxis');
    touch_object(chartData, 'chart');

    chartData.xAxis = {
      ...chartData.xAxis,
      labels: {
        enabled: true,
        formatter: function() {
          return localChartData.Categories[this.value];
        },
      },
    };

    delete chartData.xAxis.categories;

    const ADDITIONAL_SPOUSE_POINT_OPTIONS = {};

    if (localChartData.HasSpouse) {
      ADDITIONAL_SPOUSE_POINT_OPTIONS[localChartData.SpouseLifeExpectancyIndex] = {
        subjectName: localChartData.SpouseName,
        marker: {
          enabled: true,
          symbol: 'circle',
          radius: 4,
          fillColor: null,
          lineColor: null,
          lineWidth: 2,
          states: {
            hover: {
              lineWidthPlus: 0,
              radiusPlus: 0,
              enabled: true,
              fillColor: null,
              lineColor: null,
              lineWidth: 4,
              radius: 8,
            },
          },
        },
      };

      if (localChartData.SpouseRetirementAge <= localChartData.SpouseLifeExpectancy) {
        ADDITIONAL_SPOUSE_POINT_OPTIONS[localChartData.SpouseRetirementIndex] = {
          marker: {
            enabled: true,
            symbol: 'circle',
            radius: 4,
            fillColor: COLOR_BLUE,
            lineColor: COLOR_BLUE,
            lineWidth: 2,
            states: {
              hover: {
                lineWidthPlus: 0,
                radiusPlus: 0,
                enabled: true,
                fillColor: COLOR_BLUE,
                lineColor: COLOR_LIGHT_BLUE,
                lineWidth: 4,
                radius: 8,
              },
            },
          },
        };
      }
    }

    const ADDITIONAL_POINT_OPTIONS = {
      ...ADDITIONAL_SPOUSE_POINT_OPTIONS,
      [localChartData.PrincipalLifeExpectancyIndex]: {
        subjectName: localChartData.PrincipalName,
        marker: {
          enabled: true,
          symbol: 'circle',
          radius: 4,
          fillColor: null,
          lineColor: null,
          lineWidth: 2,
          states: {
            hover: {
              lineWidthPlus: 0,
              radiusPlus: 0,
              enabled: true,
              fillColor: null,
              lineColor: null,
              lineWidth: 4,
              radius: 8,
            },
          },
        },
      },
    };

    if (localChartData.PrincipalRetirementAge <= localChartData.PrincipalLifeExpectancy) {
      ADDITIONAL_POINT_OPTIONS[localChartData.PrincipalRetirementIndex] = {
        marker: {
          enabled: true,
          symbol: 'circle',
          radius: 4,
          fillColor: COLOR_BLUE,
          lineColor: COLOR_BLUE,
          lineWidth: 2,
          states: {
            hover: {
              lineWidthPlus: 0,
              radiusPlus: 0,
              enabled: true,
              fillColor: COLOR_BLUE,
              lineColor: COLOR_LIGHT_BLUE,
              lineWidth: 4,
              radius: 8,
            },
          },
        },
      };
    }

    const getMarkerAndHover = (point, index) => {
      if (isViewMobile()) {
        return { marker: null };
      }
      const object = ADDITIONAL_POINT_OPTIONS[index];
      if (object) {
        const solidColor = point > 0 ? COLOR_GREEN : COLOR_RED;
        const slightlyTransparentColor = point > 0 ? COLOR_LIGHT_GREEN : COLOR_LIGHT_RED;

        if (!object.marker.fillColor) {
          object.marker.fillColor = solidColor;
        }
        if (!object.marker.lineColor) {
          object.marker.lineColor = solidColor;
        }
        if (object.marker.states) {
          if (!object.marker.states.hover.fillColor) {
            object.marker.states.hover.fillColor = solidColor;
          }
          if (!object.marker.states.hover.lineColor) {
            object.marker.states.hover.lineColor = slightlyTransparentColor;
          }
        }
      }
      return object || { marker: null };
    };

    chartData.series[CURRENT_SERIES_INDEX].data = localChartData.CurrentSeries.map(
      (point, index) => {
        if (!ADV.util.isNumeric(point) || point < 0) {
          localChartData.CurrentSeries[index] = point = 0;
        }
        if (maxY < point) {
          maxY = point;
        }
        return {
          y: point,
          year: yearCalculator(localChartData.Categories, index),
          isCurrentPlan: true,
          principalAge: ageCalculator(
            localChartData.PrincipalLifeExpectancy,
            localChartData.PrincipalLifeExpectancyIndex,
            index,
          ),
          spouseAge: localChartData.HasSpouse
            ? ageCalculator(
                localChartData.SpouseLifeExpectancy,
                localChartData.SpouseLifeExpectancyIndex,
                index,
              )
            : '?',
          hasSpouse: localChartData.HasSpouse,
        };
      },
    );

    chartData.series[RECOMMENDED_SERIES_INDEX].data = localChartData.RecommendedSeries.map(
      (point, index) => {
        if (!ADV.util.isNumeric(point) || point < 0) {
          localChartData.RecommendedSeries[index] = point = 0;
        }
        if (maxY < point) {
          maxY = point;
        }
        return {
          y: point,
          year: yearCalculator(localChartData.Categories, index),
          isCurrentPlan: false,
          principalAge: ageCalculator(
            localChartData.PrincipalLifeExpectancy,
            localChartData.PrincipalLifeExpectancyIndex,
            index,
          ),
          spouseAge: localChartData.HasSpouse
            ? ageCalculator(
                localChartData.SpouseLifeExpectancy,
                localChartData.SpouseLifeExpectancyIndex,
                index,
              )
            : '?',
          hasSpouse: localChartData.HasSpouse,
        };
      },
    );

    chartData.series[RECOMMENDED_SERIES_INDEX].zIndex = seriesZIndex(
      chartData,
      RECOMMENDED_SERIES_INDEX,
      CURRENT_SERIES_INDEX,
    );

    chartData.series[CURRENT_SERIES_INDEX].zIndex = seriesZIndex(
      chartData,
      CURRENT_SERIES_INDEX,
      RECOMMENDED_SERIES_INDEX,
    );

    chartData.series[0].data = [
      [localChartData.Categories[0], 0],
      [localChartData.Categories[0], localChartData.RecommendedSeries[0]],
    ];

    if (localChartData.IsPrint) {
      chartData.series[RECOMMENDED_SERIES_INDEX].fillColor = '#e5f8ff';
      chartData.series.forEach(serie => {
        serie.animation = false;
      });
    } else {
      let isSlightlyTransparent = false;
      // whenever the proposed is behind the current, we make the current slightly transparent so you can see the proposed behind it
      if (
        chartData.series[RECOMMENDED_SERIES_INDEX].zIndex <
        chartData.series[CURRENT_SERIES_INDEX].zIndex
      ) {
        const enableIndexes = [
          localChartData.PrincipalRetirementIndex,
          localChartData.SpouseRetirementIndex,
          localChartData.PrincipalLifeExpectancyIndex,
          localChartData.SpouseLifeExpectancyIndex,
        ];
        enableIndexes.forEach(enableIndex => {
          if (
            localChartData.RecommendedSeries[enableIndex] * 0.95 <
            localChartData.CurrentSeries[enableIndex]
          ) {
            chartData.series[CURRENT_SERIES_INDEX].fillColor = 'rgba(90,190,228,0.8)';
            dashIsBetweenGraphs = true;
            chartData.series[RECOMMENDED_MARKERS_AND_HOVERS_INDEX].zIndex =
              chartData.series[RECOMMENDED_SERIES_INDEX].zIndex + 1;
            isSlightlyTransparent = true;
          }
        });
      }

      if (!isSlightlyTransparent) {
        chartData.series[CURRENT_SERIES_INDEX].fillColor = '#5abee4';
      }
    }

    // Decide where the colored circle markers should render
    chartData.series[CURRENT_MARKERS_AND_HOVERS_INDEX].data = chartData.series[
      CURRENT_SERIES_INDEX
    ].data.map((point, index) => {
      let extra = {};

      if (
        localChartData.RecommendedSeries[index] === point.y &&
        chartData.series[RECOMMENDED_SERIES_INDEX].zIndex <
          chartData.series[CURRENT_SERIES_INDEX].zIndex
      ) {
        extra = getMarkerAndHover(point.y, index);

        if (!isViewMobile()) {
          extra.isProposed = true;
        }
      }

      return {
        ...point,
        ...extra,
      };
    });

    if (isCurrentPlan) {
      chartData.series[RECOMMENDED_MARKERS_AND_HOVERS_INDEX].name = 'Current Plan';
    }

    chartData.series[RECOMMENDED_MARKERS_AND_HOVERS_INDEX].data = chartData.series[
      RECOMMENDED_SERIES_INDEX
    ].data.map((point, index) => {
      return {
        ...point,
        ...getMarkerAndHover(point.y, index),
      };
    });

    // leave only markers for the viewed plan
    Object.keys(ADDITIONAL_POINT_OPTIONS).forEach(pointIndex => {
      const seriesIndex = isCurrentPlan
        ? RECOMMENDED_MARKERS_AND_HOVERS_INDEX
        : CURRENT_MARKERS_AND_HOVERS_INDEX;
      const seriesData = chartData.series[seriesIndex].data;

      seriesData[pointIndex] = { ...seriesData[pointIndex], marker: null };
    });

    chartData.yAxis.max = goalData.Chart.IsEmpty ? 10_000 : maxY;
    chartData.yAxis.ceiling = maxY + 50;
    chartData.yAxis.offset = 50;

    chartData.chart.events = {
      load: renderChartLabels,
      redraw: renderChartLabels,
    };

    localProps = props;
    localDescriptor = chartData;

    return chartData;
  },
);

export const proposedPlan = (state, ownProps) => {
  return ownProps.proposed_plan || 'Proposed Plan';
};
export const loading = (state, { clientId }) => {
  const { loading } = state.RetirementAssetsChart[clientId] || {};
  if (typeof loading === 'undefined') {
    return true;
  }
  return loading;
};
export const showLoading = (state, { clientId }) => {
  return !!(state.RetirementAssetsChart[clientId] || {}).showLoading;
};
